/**
 * User form submission hook
 * Handles create and edit user operations with proper error handling and validation
 */

import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../../../hooks/use-toast';
import { supabase } from '@/lib/supabaseClient';
import { updateUserProfileOnServer, UpdateUserProfilePayload } from '../../../data/users';
import { invokeEdgeFunctionSafely } from '@/lib/supabase-functions-wrapper';
import { SupportedLanguage, syncLanguageToStorage } from '@/lib/language-service';
import { componentLogger } from '@/lib/logging';
import type { UserProfile, Role } from '@/lib/types/core';
import {
  UserFormData,
  CreateUserFormData,
  EditUserFormData,
  AssignmentPermission,
  NO_COMPANY_SENTINEL
} from '../UserDialogTypes';

interface UseUserFormSubmissionProps {
  form: UseFormReturn<UserFormData>;
  userToEdit?: UserProfile | null;
  mode: 'create' | 'edit';
  userContext: UserProfile | null;
  isInvokerCompanyAdmin: boolean;
  invokerCompanyId?: string;
  canEditProfileFields: boolean;
  onUserMutated?: (_user: UserProfile) => void;
  onOpenChange: (_open: boolean) => void;
  syncAssignmentPermissions: (
    _userId: string,
    _newPermissions: AssignmentPermission[],
    _oldPermissions: AssignmentPermission[]
  ) => Promise<string[]>;
  syncEmailPreferences: (
    _userId: string,
    _assignmentPermissions: AssignmentPermission[],
    _forceUpdate?: boolean
  ) => Promise<void>;
  originalPermissions: AssignmentPermission[];
}

export const useUserFormSubmission = ({
  form,
  userToEdit,
  mode,
  userContext,
  isInvokerCompanyAdmin,
  invokerCompanyId,
  canEditProfileFields,
  onUserMutated,
  onOpenChange,
  syncAssignmentPermissions,
  syncEmailPreferences,
  originalPermissions,
}: UseUserFormSubmissionProps) => {
  const { t } = useTranslation();
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [duplicateEmailError, setDuplicateEmailError] = useState(false);
  const isEdit = mode === 'edit';

  // Create user handler
  const handleCreateUser = async (values: CreateUserFormData) => {
    if (!values.email) {
      toast({
        title: t('usersActionDialog.toast.emailMissingTitle'),
        description: t('usersActionDialog.toast.emailMissingDescription'),
        variant: 'destructive'
      });
      return;
    }

    if (isInvokerCompanyAdmin && values.companyId !== invokerCompanyId) {
      toast({
        title: t('common.errorTitle'),
        description: t('usersActionDialog.toast.companyAdminAssignOwnCompanyOnly'),
        variant: 'destructive'
      });
      return;
    }

    const payload = {
      email: values.email,
      full_name: (values.firstName || values.lastName)
                 ? `${values.firstName || ''} ${values.lastName || ''}`.trim()
                 : undefined,
      phone_number: values.phoneNumber || undefined,
      role: values.app_role,
      is_representative: values.isRepresentative,
      company_id: values.companyId === NO_COMPANY_SENTINEL || values.companyId === undefined
                  ? null
                  : values.companyId,
      locale: values.language || 'en',
      send_invite_email: true
    };

    try {
      const { data: newUser, error: createError } = await supabase.functions.invoke('create-user', {
        body: payload
      });

      if (createError) {
        throw {
          name: 'FunctionsHttpError',
          data: createError,
          message: createError.message || createError.error || 'Function error',
          status: createError.status || 500
        };
      }

      // Handle assignment permissions
      const currentPermissions = form.getValues('assignmentPermissions') || [];
      componentLogger.debug('useUserFormSubmission', 'Processing assignment permissions for new user', {
        userId: newUser?.id,
        permissionsCount: currentPermissions.length,
        permissions: currentPermissions
      });

      if (currentPermissions.length > 0 && newUser?.id) {
        componentLogger.debug('useUserFormSubmission', 'Calling syncAssignmentPermissions for new user', {
          userId: newUser.id,
          permissionsToAdd: currentPermissions.length
        });
        const permissionErrors = await syncAssignmentPermissions(newUser.id, currentPermissions, []);

        try {
          await syncEmailPreferences(newUser.id, currentPermissions, true);
        } catch (emailError) {
          componentLogger.error('useUserFormSubmission', 'Failed to save email preferences for new user', emailError);
          permissionErrors.push('Email preferences sync failed');
        }

        if (permissionErrors.length > 0) {
          toast({
            title: t('usersActionDialog.toast.partialSuccessTitle'),
            description: `User was created but some assignment permissions failed: ${permissionErrors.join(', ')}`,
            variant: 'default',
          });
        }
      }

      const userInviteCode = newUser?.invitation_token;
      const description = userInviteCode
        ? `User ${`${values.firstName} ${values.lastName || ''}`.trim()} has been created. Invitation code: ${userInviteCode.substring(0, 8)}...`
        : t('usersActionDialog.toast.createUserSuccessDescription', `User {fullName} has been successfully created.`, {
            fullName: `${values.firstName} ${values.lastName || ''}`.trim()
          });

      toast({
        title: t('usersActionDialog.toast.createUserSuccessTitle'),
        description
      });

      if (onUserMutated && newUser) {
        onUserMutated(newUser as UserProfile);
      }
      form.reset();
      onOpenChange(false);

    } catch (error: unknown) {
      componentLogger.error('useUserFormSubmission', 'Failed to create user', error);

      let errorMessage = t('common.unknownError');
      const typedError = error as {
        data?: unknown;
        message?: string;
        status?: number;
      };

      // Check for duplicate email error (409 status)
      // FunctionsHttpError stores status in error.context.status
      // Since we wrapped it with { data: createError }, we need to check data.context.status
      let statusCode: number | undefined;
      if (typedError.data && typeof typedError.data === 'object') {
        const dataObj = typedError.data as Record<string, unknown>;
        if ('context' in dataObj && dataObj['context'] && typeof dataObj['context'] === 'object') {
          const context = dataObj['context'] as Record<string, unknown>;
          if ('status' in context && typeof context['status'] === 'number') {
            statusCode = context['status'];
          }
        }
        // Also check if error message indicates duplicate email
        if ('error' in dataObj && dataObj['error'] === 'Email already exists') {
          setDuplicateEmailError(true);
          return; // Don't show toast for duplicate email - it will be shown in the form
        }
      }

      // Check status code
      if (statusCode === 409 || typedError.status === 409) {
        setDuplicateEmailError(true);
        return; // Don't show toast for duplicate email - it will be shown in the form
      }

      if (typedError.data && typeof typedError.data === 'object' && (typedError.data as { error?: string }).error) {
        errorMessage = (typedError.data as { error: string }).error;
      } else if (typedError.message && typedError.message !== 'Function error') {
        errorMessage = typedError.message;
      }

      toast({
        title: t('usersActionDialog.toast.createUserErrorTitle'),
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };

  // Edit user handler
  const handleEditUser = async (values: EditUserFormData) => {
    if (!userToEdit) return;

    const payload: UpdateUserProfilePayload = {
      ...(values.firstName !== undefined && { first_name: values.firstName }),
      ...(values.lastName !== undefined && { last_name: values.lastName }),
      ...(values.phoneNumber !== undefined && { phone_number: values.phoneNumber }),
      ...(values.language !== undefined && { language: values.language }),
      ...(values.title !== undefined && { title: values.title }),
    };

    // Add admin fields if user has permission
    if (canEditProfileFields) {
      payload.app_role = values.app_role as Role;
      payload.company_id = values.companyId === NO_COMPANY_SENTINEL || values.companyId === undefined || values.companyId === ''
                        ? null
                        : values.companyId;
      if (values.status !== undefined) {
        payload.status = values.status;
      }
      if (values.isRepresentative !== undefined) {
        payload.is_representative = values.isRepresentative;
      }
    }

    // Remove undefined values
    Object.keys(payload).forEach(keyStr => {
      const key = keyStr as keyof UpdateUserProfilePayload;
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    if (Object.keys(payload).length === 0) {
      toast({
        title: 'No Changes',
        description: 'No information was changed.',
        variant: 'default'
      });
      onOpenChange(false);
      return;
    }

    try {
      const { message: _message, profile: updatedProfile } = await updateUserProfileOnServer(userToEdit.id, payload);

      // Handle profile picture upload if provided
      const profilePictureErrors: string[] = [];
      const profilePictureData = form.getValues('profilePictureData');
      if (profilePictureData) {
        try {
          componentLogger.debug('useUserFormSubmission', 'Uploading profile picture', {
            userId: userToEdit.id,
            fileName: profilePictureData.fileName
          });

          const { error: pictureError } = await invokeEdgeFunctionSafely('update-profile-picture', {
            body: {
              profileId: userToEdit.id,
              tempStoragePath: profilePictureData.tempStoragePath,
              fileName: profilePictureData.fileName,
              mimeType: profilePictureData.mimeType,
              sizeBytes: profilePictureData.sizeBytes,
            }
          });

          if (pictureError) {
            componentLogger.error('useUserFormSubmission', 'Failed to upload profile picture', pictureError);
            profilePictureErrors.push('Profile picture upload failed');
          } else {
            componentLogger.debug('useUserFormSubmission', 'Profile picture uploaded successfully', {
              userId: userToEdit.id
            });
          }
        } catch (pictureUploadError) {
          componentLogger.error('useUserFormSubmission', 'Error uploading profile picture', pictureUploadError);
          profilePictureErrors.push('Profile picture upload error');
        }
      }

      // Sync assignment permissions and wait for completion
      const currentPermissions = form.getValues('assignmentPermissions') || [];
      const permissionErrors = await syncAssignmentPermissions(userToEdit.id, currentPermissions, originalPermissions);

      // Sync email preferences and wait for completion
      try {
        await syncEmailPreferences(userToEdit.id, currentPermissions);
      } catch (emailError) {
        componentLogger.error('useUserFormSubmission', 'Failed to sync email preferences', emailError);
        permissionErrors.push('Email preferences sync failed');
      }

      // Apply language change if user is editing themselves
      if (userContext?.id === userToEdit.id && values.language) {
        syncLanguageToStorage(values.language as SupportedLanguage);
      }

      // Combine all errors
      const allErrors = [...profilePictureErrors, ...permissionErrors];

      // Show appropriate toast based on whether there were any errors
      if (allErrors.length > 0) {
        toast({
          title: t('usersActionDialog.toast.partialUpdateTitle'),
          description: t('usersActionDialog.toast.partialUpdateDescription', {
            errors: allErrors.join(', ')
          }),
          variant: 'default',
        });
      } else {
        toast({
          title: t('usersActionDialog.toast.updateSuccessTitle'),
          description: t('usersActionDialog.toast.updateSuccessDescription')
        });
      }

      // Call mutation callback and close dialog AFTER all operations complete
      if (onUserMutated) {
        onUserMutated(updatedProfile);
      }
      form.reset();
      onOpenChange(false);

    } catch (error: unknown) {
      componentLogger.error('useUserFormSubmission', 'Failed to update user', error);
      toast({
        title: t('usersActionDialog.toast.updateErrorTitle'),
        description: (error as Error).message || t('common.unknownError'),
        variant: 'destructive'
      });
    }
  };

  // Main form submission handler
  const onSubmit = async (values: UserFormData) => {
    setIsSubmittingForm(true);

    try {
      if (mode === 'create') {
        await handleCreateUser(values as CreateUserFormData);
      } else if (isEdit && userToEdit) {
        await handleEditUser(values as EditUserFormData);
      }
    } finally {
      setIsSubmittingForm(false);
    }
  };

  // Function to clear duplicate email error
  const clearDuplicateEmailError = () => {
    setDuplicateEmailError(false);
  };

  return {
    isSubmittingForm,
    onSubmit,
    handleCreateUser,
    handleEditUser,
    duplicateEmailError,
    clearDuplicateEmailError,
  };
};